import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import React, { ChangeEvent, KeyboardEvent, useRef } from "react";
import { IManagedDbForeignKey } from "../../services/table-editor-service";
import ChainSvg from "../../svgs/small-icons/chain.svg";
import KuikaCheckbox from "../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDatePicker from "../ui-kit/kuika-date-picker/kuika-date-picker";
import BinaryCellEditor from "./binary-cell-editor";
import { datetimeHasFractionalSeconds } from "./datetime-format";
import FkPicker from "./fk-picker";
import ImageCellEditor from "./image-cell-editor";
import LocationCellEditor from "./location-cell-editor";
import TriStateCheckbox, { TriStateValue } from "./tri-state-checkbox";

export interface ICellEditorProps {
  column: IDatabaseColumnModel;
  value: unknown;
  disabled?: boolean;
  // FK metadata when the column is a foreign-key source — enables the Browse
  // adornment that opens FkPicker.
  foreignKey?: IManagedDbForeignKey;
  // Short identifier for the row — used by binary / image cells to
  // suffix the download filename so multiple rows of the same table
  // don't all download to `<column>.<ext>` and stack as `(1)`,
  // `(2)`. Typically the first PK column's value; null on insert rows
  // (no PK yet) → filename falls back to just `<column>.<ext>`.
  rowKey?: string | null;
  onChange: (next: unknown) => void;
  onBlur?: () => void;
  // Optional callback the grid wires up so the cell can offer an
  // "Expand" adornment for long-text columns. When set, a button
  // appears alongside the input on String cells; clicking it opens
  // the cell-expander modal at the parent. Replaced the old
  // double-click affordance — buttons are discoverable, double-click
  // wasn't.
  onRequestExpand?: () => void;
}

const isBooleanType = (type: string | null | undefined): boolean => type === "Boolean";
const isGuidType = (type: string | null | undefined): boolean => type === "Guid";

// Int16 / Int32 / Double / AutoIncrement all fit in JS Number cleanly. Int64
// and Decimal don't — beyond 2^53 a Number drops precision and you get the
// wrong value back when JSON.stringify round-trips it. Those columns use a
// text editor that keeps the user's input as a string end-to-end; the
// backend's NormalizeParameterValue parses them to long/decimal at the bind
// site so the ADO provider still sees the right CLR type.
const isSafeNumericType = (type: string | null | undefined): boolean => {
  return type === "Int16" || type === "Int32" || type === "Double" || type === "AutoIncrement";
};

const isHighPrecisionNumericType = (type: string | null | undefined): boolean => {
  return type === "Int64" || type === "Decimal";
};

const isDateOnlyType = (type: string | null | undefined): boolean => type === "Date";
const isNaiveDatetimeType = (type: string | null | undefined): boolean => type === "Datetime";
const isDatetimeOffsetType = (type: string | null | undefined): boolean => type === "DatetimeOffset";

// Binary blob types — Image, Signature, FileContent, byte arrays. The
// inline editor for these is a file picker that stores the file's
// base64 (without the data-URI prefix), matching the wire format the
// rest of Kuika uses for binary columns. We accept the cell will hold
// large strings; the grid renders a compact "filename · size" pill so
// the cell stays readable.
const isBinaryBlobType = (type: string | null | undefined): boolean => {
  return (
    type === "Image" ||
    type === "Signature" ||
    type === "FileContent" ||
    type === "ByteArray" ||
    type === "VarBinary" ||
    type === "Binary" ||
    type === "Bytea"
  );
};

// Location is a JSON object `{latitude, longitude}` stored as a string
// in nvarchar. The editor is a tight lat / lng pair — far cleaner than
// asking the user to type JSON into a grid cell, and matches the wire
// format the LocationPicker writes on the runtime side.
const isLocationType = (type: string | null | undefined): boolean => type === "Location";

// Object — Kuika's catch-all fallback type for shapes the schema
// doesn't pin down. There's no canonical wire format; typing freeform
// JSON for a cell whose shape we don't know is a recipe for silent
// corruption. We display read-only and leave editing to the proper
// purpose-built widget on the deployed app.
const isUnstructuredType = (type: string | null | undefined): boolean => type === "Object";

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

const placeholderFor = (column: IDatabaseColumnModel): string => (column.nullable ? "null" : "");

// crypto.randomUUID() is on every browser the designer supports (Chrome 92+,
// Firefox 95+, Safari 15.4+). The fallback only fires if someone runs the
// designer in something ancient.
const newGuid = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c: string): string => {
    const r: number = Math.floor(Math.random() * 16);
    const v: number = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const CellEditor: React.FC<ICellEditorProps> = (props: ICellEditorProps): JSX.Element => {
  const { column, value, disabled, foreignKey, rowKey, onChange, onBlur, onRequestExpand } = props;
  const dataType: string = column.kuikaDataType ?? "";
  // Screen-reader label for every inline input. Without this, SR
  // users editing a cell hear "edit" or just the value with no
  // hint of which column they're in. The visible label is the
  // column header far above — too far up the table for an SR's
  // single-cell focus to convey naturally.
  const cellAriaLabel: string = column.name;

  // ── Esc-to-revert (Numbers / Excel convention) ──────────────────
  // Capture the cell's value on focus; if the user presses Esc
  // during the edit session, revert to that captured value and
  // blur. Apple-bar UX expectation — without this, Esc only blurs
  // and the live-typed changes stay committed (every keystroke
  // already fired onChange into the reducer).
  //
  // Session-start = the value when the input GAINED focus. A cell
  // that was already dirty before this session keeps its prior
  // dirty value (only this session's typing is undone). The grid
  // editor's reducer detects "value back to original" downstream
  // and collapses the pending entry, so a perfect revert to the
  // un-edited state cleans up automatically.
  const sessionStartValueRef = useRef<unknown>(value);
  const handleEditableFocus = (): void => {
    sessionStartValueRef.current = value;
  };
  const handleEditableKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Escape") {
      return;
    }
    // Don't let the grid's tbody Escape handler also fire — we own
    // the revert; the blur happens after via the input's natural
    // path. preventDefault keeps the default form-reset semantics
    // from interfering.
    e.preventDefault();
    e.stopPropagation();
    onChange(sessionStartValueRef.current);
    (e.target as HTMLInputElement).blur();
  };

  if (isUnstructuredType(dataType)) {
    return (
      <span
        className="kuika-table-editor__grid-cell-static kuika-table-editor__grid-cell-null"
        title="Object values have no canonical shape — edit through the proper widget on a form."
      >
        object — not editable here
      </span>
    );
  }

  if (isBinaryBlobType(dataType)) {
    // Image / Signature get the rich image editor — thumbnail in the
    // cell, click for a full-size lightbox, MIME-aware data URI,
    // image-only file picker. Other binary types stay on the generic
    // chip + picker since we don't know what's in the bytes.
    if (dataType === "Image" || dataType === "Signature") {
      return (
        <ImageCellEditor
          value={value}
          dataType={dataType}
          columnName={column.name}
          rowKey={rowKey}
          disabled={disabled}
          onChange={onChange}
          onBlur={onBlur}
        />
      );
    }
    return (
      <BinaryCellEditor
        value={value}
        columnName={column.name}
        rowKey={rowKey}
        // varbinary(N) declares a byte ceiling; the column model
        // surfaces it as maxLength. Pass it through so the cell can
        // reject oversize picks before the SQL "would be truncated"
        // error. `-1` (varbinary(max)) means "unlimited".
        columnMaxBytes={typeof column.maxLength === "number" ? column.maxLength : null}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  if (isLocationType(dataType)) {
    return <LocationCellEditor value={value} disabled={disabled} onChange={onChange} onBlur={onBlur} />;
  }

  if (isBooleanType(dataType)) {
    // Nullable Boolean columns use a tri-state control because `null`
    // and `false` are different — collapsing them to "unchecked"
    // would make every nullable Boolean cell unsafe to edit (no way
    // to set null back; no way to tell apart). Non-nullable Boolean
    // columns keep the simpler two-state checkbox.
    if (column.nullable) {
      let triValue: TriStateValue;
      if (value === true || value === "true") {
        triValue = true;
      } else if (value === false || value === "false") {
        triValue = false;
      } else {
        triValue = null;
      }
      return (
        <span className="kuika-table-editor__cell-shell kuika-table-editor__boolean-cell">
          <TriStateCheckbox
            value={triValue}
            disabled={disabled}
            onBlur={onBlur}
            onChange={(next: TriStateValue): void => onChange(next)}
          />
        </span>
      );
    }
    const checked: boolean = value === true || value === "true";
    return (
      <span className="kuika-table-editor__cell-shell kuika-table-editor__boolean-cell">
        <KuikaCheckbox
          checked={checked}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => onChange(e.target.checked)}
        />
      </span>
    );
  }

  if (isSafeNumericType(dataType)) {
    return (
      <div className="kuika-table-editor__cell-shell">
        <input
          type="number"
          className="kuika-table-editor__grid-cell-input"
          aria-label={cellAriaLabel}
          disabled={disabled}
          placeholder={placeholderFor(column)}
          value={stringifyValue(value)}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            const raw: string = e.target.value;
            if (raw === "") {
              onChange(null);
              return;
            }
            const num: number = Number(raw);
            onChange(Number.isFinite(num) ? num : raw);
          }}
          onFocus={handleEditableFocus}
          onKeyDown={handleEditableKeyDown}
          onBlur={onBlur}
        />
        {foreignKey && (
          <FkPicker
            refTableName={foreignKey.refTableName}
            refTableSchema={foreignKey.refTableSchema}
            refColumnName={foreignKey.refColumnName}
            sourceColumnName={column.name}
            currentValue={value}
            onPick={(picked: unknown): void => onChange(picked)}
          >
            <button
              type="button"
              className="kuika-table-editor__cell-adorn"
              title={`Browse ${foreignKey.refTableName}`}
              onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void => e.preventDefault()}
            >
              <ChainSvg width={11} height={11} />
              <span>Browse</span>
            </button>
          </FkPicker>
        )}
      </div>
    );
  }

  // High-precision numerics (Int64, Decimal) — text input + inputMode so
  // mobile keyboards still show digits, but we never feed the raw through
  // Number() and lose precision. The string travels untouched to the server,
  // where NormalizeParameterValue parses it to long/decimal.
  if (isHighPrecisionNumericType(dataType)) {
    return (
      <input
        type="text"
        className="kuika-table-editor__grid-cell-input"
        aria-label={cellAriaLabel}
        disabled={disabled}
        inputMode={dataType === "Decimal" ? "decimal" : "numeric"}
        pattern={dataType === "Decimal" ? "-?\\d*(\\.\\d+)?" : "-?\\d*"}
        placeholder={placeholderFor(column)}
        value={stringifyValue(value)}
        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
          const raw: string = e.target.value;
          onChange(raw === "" ? null : raw);
        }}
        onFocus={handleEditableFocus}
        onKeyDown={handleEditableKeyDown}
        onBlur={onBlur}
      />
    );
  }

  if (isDateOnlyType(dataType)) {
    return (
      <KuikaDatePicker
        mode="date"
        variant="cell"
        size="sm"
        disabled={disabled}
        placeholder={placeholderFor(column)}
        value={stringifyValue(value)}
        // Surface an explicit × on nullable columns so the user has a
        // consistent set-to-null affordance across every browser.
        showClear={column.nullable !== false}
        onChange={(next: string | null): void => onChange(next)}
        onBlur={onBlur}
      />
    );
  }

  if (isNaiveDatetimeType(dataType)) {
    // Datetime (naive — no offset). Wall clock travels as-is through the
    // editor and into the database. step driven by the source data so
    // rows with fractional seconds keep their precision (without
    // step=0.001 Chrome / Safari truncate ms silently).
    const rawNaive: string = stringifyValue(value);
    return (
      <KuikaDatePicker
        mode="datetime"
        variant="cell"
        size="sm"
        disabled={disabled}
        placeholder={placeholderFor(column)}
        value={rawNaive}
        hasFractionalSeconds={datetimeHasFractionalSeconds(rawNaive)}
        showClear={column.nullable !== false}
        onChange={(next: string | null): void => onChange(next)}
        onBlur={onBlur}
      />
    );
  }

  if (isDatetimeOffsetType(dataType)) {
    // DateTimeOffset column — DB-tool semantics: display the wall clock
    // stored in the row (KuikaDatePicker strips trailing offset / Z),
    // save with `+00:00` so the server's
    // `DateTimeOffset.TryParse(..., AssumeUniversal | AdjustToUniversal)`
    // stores the exact wall clock the user typed, in UTC. Pre-fix we
    // stamped the user's BROWSER offset on every save — silently
    // relocating rows by N hours per editor. A DB tool should never
    // do that.
    //
    // The wrapping flex + UTC chip make that contract visible — the
    // user never has to wonder "what timezone is this picker in".
    const rawOffset: string = stringifyValue(value);
    return (
      <span className="kuika-table-editor__cell-shell kuika-table-editor__dto-cell">
        <KuikaDatePicker
          mode="datetimeoffset"
          variant="cell"
          size="sm"
          disabled={disabled}
          placeholder={placeholderFor(column)}
          value={rawOffset}
          hasFractionalSeconds={datetimeHasFractionalSeconds(rawOffset)}
          showClear={column.nullable !== false}
          onChange={(next: string | null): void => {
            if (next === null) {
              onChange(null);
              return;
            }
            onChange(next + "+00:00");
          }}
          onBlur={onBlur}
        />
        <span
          className="kuika-table-editor__utc-tag"
          aria-label="Stored as UTC"
          title="DateTimeOffset values are entered and stored in UTC, not your local timezone."
        >
          UTC
        </span>
      </span>
    );
  }

  // String / Guid path — adornments live on the right of the input. onMouseDown
  // preventDefault stops the input losing focus before our click handler runs.
  //
  // FK columns suppress the New-GUID affordance even when the underlying type
  // is Guid: a random uuid wouldn't reference an existing row in the parent
  // table and the insert/update would fail on the FK constraint. Browse is
  // the only sensible action.
  const showFkBrowse: boolean = foreignKey !== undefined;
  const showGuidGen: boolean = isGuidType(dataType) && !showFkBrowse;
  // Only String columns benefit from the expander — a Guid is a fixed
  // 36 chars and fits any cell. Gate on the callback being wired so the
  // adornment doesn't show in test seams that don't provide one.
  const showExpand: boolean = dataType === "String" && typeof onRequestExpand === "function";
  return (
    <div className="kuika-table-editor__cell-shell">
      <input
        type="text"
        className="kuika-table-editor__grid-cell-input"
        data-testid="tableEditorCellInput"
        aria-label={cellAriaLabel}
        disabled={disabled}
        placeholder={placeholderFor(column)}
        value={stringifyValue(value)}
        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
          const raw: string = e.target.value;
          onChange(isGuidType(dataType) ? (raw.trim().length === 0 ? null : raw) : raw);
        }}
        onFocus={handleEditableFocus}
        onKeyDown={handleEditableKeyDown}
        onBlur={onBlur}
      />
      {showExpand && (
        <button
          type="button"
          className="kuika-table-editor__cell-adorn"
          title="Open in a larger editor"
          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void => e.preventDefault()}
          onClick={(): void => onRequestExpand?.()}
        >
          <span>Expand</span>
        </button>
      )}
      {showGuidGen && (
        <button
          type="button"
          className="kuika-table-editor__cell-adorn"
          data-testid="tableEditorNewGuidButton"
          title="Generate a new GUID for this cell"
          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void => e.preventDefault()}
          onClick={(): void => onChange(newGuid())}
        >
          <span>New GUID</span>
        </button>
      )}
      {showFkBrowse && foreignKey && (
        <FkPicker
          refTableName={foreignKey.refTableName}
          refTableSchema={foreignKey.refTableSchema}
          refColumnName={foreignKey.refColumnName}
          sourceColumnName={column.name}
          currentValue={value}
          onPick={(picked: unknown): void => onChange(picked)}
        >
          <button
            type="button"
            className="kuika-table-editor__cell-adorn"
            title={`Browse ${foreignKey.refTableName}`}
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void => e.preventDefault()}
          >
            <ChainSvg width={11} height={11} />
            <span>Browse</span>
          </button>
        </FkPicker>
      )}
    </div>
  );
};

export default CellEditor;
